import ManageAssetsService from '@/service/crmservice/manageassetsservice';

export const MANAGE_ASSETS_LIST_PATH = '/manageassets';
export const MANAGE_ASSETS_MODULE_CODE = '6005';
const NAVIGATION_CACHE_KEY = 'manageAssetsNavigationCache';
const NAVIGATION_CACHE_TTL_MS = 30000;

let pendingNavigationRequest = null;

function normalizeRoutePath(path) {
    if (path == null || path === '') {
        return '';
    }
    const raw = String(path).trim().toLowerCase();
    if (!raw) {
        return '';
    }
    return raw.startsWith('/') ? raw : `/${raw}`;
}

export function isManageAssetsListRoute(path) {
    return normalizeRoutePath(path) === MANAGE_ASSETS_LIST_PATH;
}

export function buildManageAssetDetailPath(assetId) {
    if (!assetId) {
        return MANAGE_ASSETS_LIST_PATH;
    }
    //return `${MANAGE_ASSETS_LIST_PATH}/AssetDetialView/${assetId}`;
return MANAGE_ASSETS_LIST_PATH;
}

function readNavigationCache() {
    try {
        const raw = sessionStorage.getItem(NAVIGATION_CACHE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
            sessionStorage.removeItem(NAVIGATION_CACHE_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function writeNavigationCache(payload) {
    sessionStorage.setItem(NAVIGATION_CACHE_KEY, JSON.stringify({
        ...payload,
        expiresAt: Date.now() + NAVIGATION_CACHE_TTL_MS
    }));
}

export function clearManageAssetsNavigationCache() {
    sessionStorage.removeItem(NAVIGATION_CACHE_KEY);
}

function buildAssetListRequest(overrides = {}) {
    return {
        VIEWER_ID: localStorage.getItem('userId'),
        COMPANY_ID: localStorage.getItem('companyId'),
        SORT_BY: 'desc',
        SORT_EXP: 'CreatedOn',
        PAGESIZE: 2,
        PAGENUMBER: 1,
        LANG_CODE: 'EN',
        IS_EXPORT: false,
        SEARCH_CONDITION: null,
        moduleName: MANAGE_ASSETS_MODULE_CODE,
        subModuleName: MANAGE_ASSETS_MODULE_CODE,
        viewPortId: null,
        searchFilter: null,
        widgetType: null,
        recordFilter: null,
        teamId: null,
        teamMemberId: null,
        isAllRecords: false,
        ...overrides
    };
}

function parseAssetListResponse(result) {
    if (result?.data?.statusCode !== 200 || !result?.data?.result) {
        return {
            totalRecords: 0,
            rows: []
        };
    }

    const parsed = JSON.parse(result.data.result);
    const rows = Array.isArray(parsed?.Data) ? parsed.Data : [];
    const totalRecords = rows.length > 0
        ? Number(rows[0].TotalRecord || 0)
        : 0;

    return {
        totalRecords: Number.isFinite(totalRecords) ? totalRecords : 0,
        rows
    };
}

async function requestAssetList(overrides = {}) {
    const service = new ManageAssetsService();
    const result = await service.getAllAssets(buildAssetListRequest(overrides));
    return parseAssetListResponse(result);
}

export async function fetchManageAssetsNavigationSnapshot(forceRefresh = false) {
    if (!forceRefresh) {
        const cached = readNavigationCache();
        if (cached) {
            return cached;
        }
    }

    if (!pendingNavigationRequest) {
        pendingNavigationRequest = requestAssetList()
            .then((snapshot) => {
                writeNavigationCache(snapshot);
                return snapshot;
            })
            .finally(() => {
                pendingNavigationRequest = null;
            });
    }

    return pendingNavigationRequest;
}

export async function resolveManageAssetsNavigationPath(forceRefresh = false) {
    const snapshot = await fetchManageAssetsNavigationSnapshot(forceRefresh);
    const totalRecords = Number(snapshot?.totalRecords || 0);
    const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];

    if (totalRecords === 1 && rows[0]?.Id) {
        return buildManageAssetDetailPath(rows[0].Id);
    }

    return MANAGE_ASSETS_LIST_PATH;
}

export async function fetchManageAssetRow(assetId, forceRefresh = false) {
    if (!assetId) {
        return null;
    }

    const cacheKey = `manageAssetRow:${assetId}`;
    if (!forceRefresh) {
        try {
            const raw = sessionStorage.getItem(cacheKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.expiresAt && Date.now() <= parsed.expiresAt) {
                    return parsed.row;
                }
            }
        } catch {
            // ignore cache read errors
        }
    }

    let snapshot = await requestAssetList({
        PAGESIZE: 1,
        PAGENUMBER: 1,
        searchFilter: `Id = '${assetId}'`
    });

    let row = snapshot.rows.find((item) => String(item.Id).toLowerCase() === String(assetId).toLowerCase()) || null;

    if (!row) {
        snapshot = await requestAssetList({
            PAGESIZE: 200,
            PAGENUMBER: 1
        });
        row = snapshot.rows.find((item) => String(item.Id).toLowerCase() === String(assetId).toLowerCase()) || null;
    }

    if (row) {
        sessionStorage.setItem(cacheKey, JSON.stringify({
            row,
            expiresAt: Date.now() + NAVIGATION_CACHE_TTL_MS
        }));
    }

    return row;
}
